<template>
  <div v-bind="$attrs">
    <section class="rounded-xl bg-gray-100 ps-8 pt-8">
      <div class="mb-8 flex items-center justify-between pe-12 ps-4">
        <div class="text-subject font-bold text-dark">
          {{ `${$t('hotel')} ${$t('results')}` }}
        </div>

        <div class="flex items-center">
          <JButton
            v-if="
              hotelSearchResultStore.hotelsList.length &&
              !isLoadingSearchAndFilter
            "
            type="button"
            size="xs"
            color="secondary"
            variant="tonal"
            class="me-8"
            @click="
              $router.push({
                path: '/hotels-map',
              })
            "
          >
            {{ $t('search.exploreOnMap') }}
          </JButton>

          <JButton
            v-if="
              !isLoadingSearchAndFilter && !hotelSearchResultStore.isSearchEmpty
            "
            type="button"
            size="xs"
            color="secondary"
            icon="filter"
            variant="tonal"
            @click="!hotelSearchResultStore.isLoading && (dialog = true)"
          />
        </div>
      </div>

      <MultiCitySearchResultSelector
        v-if="
          searchPanelHistory.history.isMultiDestination &&
          !hotelSearchResultStore.isSearchEmpty
        "
        :modelValue="city"
        :disabled="isLoadingSearchAndFilter || hotelFiltersIsLoading"
        includeHotelOptions
        @update:model-value="onSelectCity"
      />

      <div
        v-if="
          !isLoadingSearchAndFilter &&
          hotelSearchResultStore.totalHotelCount > 0
        "
        class="my-12 flex items-center justify-between pe-12 ps-4"
      >
        <span class="font-bold">{{ $t('search.chooseYourHotel') }}</span>
        <span class="font-medium text-gray-600">
          {{
            $t('search.nResults', {
              count: hotelSearchResultStore.totalHotelCount,
            })
          }}
        </span>
      </div>

      <!-- CAROUSEL -->
      <div ref="swiperElementRef" class="swiper">
        <div class="swiper-wrapper">
          <template
            v-if="
              hotelSearchResultStore.hotelsList.length ||
              isLoadingSearchAndFilter
            "
          >
            <template v-if="isLoadingSearchAndFilter">
              <div v-for="i in 4" :key="i" class="swiper-slide">
                <HotelCardSkeleton />
              </div>
            </template>

            <template v-else-if="!isLoadingSearchAndFilter">
              <div
                v-for="(hotel, index) in hotelSearchResultStore.hotelsList"
                :key="index"
                class="swiper-slide"
              >
                <HotelCard
                  v-if="Math.abs(index - swiperSlideIndex) < 3"
                  :key="hotel.uniqueId"
                  :hotel="hotel"
                  @select="handle"
                  @show-detail="openHotelDetail(hotel.searchOption)"
                />
              </div>

              <template
                v-if="
                  hotelSearchResultStore.hotelsList.length !==
                  hotelSearchResultStore.totalHotelCount
                "
              >
                <div
                  v-for="i in MINIMUM_RESULT_SIZE"
                  :key="i"
                  class="swiper-slide"
                >
                  <HotelCardSkeleton class="h-full" />
                </div>
              </template>
            </template>
          </template>

          <!-- NO RESULT -->
          <template v-else>
            <NoSearchResult
              :image="{ src: '/hotel.svg', alt: 'Hotel' }"
              :description="$t('search.noResultFound', { title: $t('hotel') })"
              class="w-full"
            />
          </template>
        </div>
      </div>
    </section>
  </div>

  <!-- FILTER -->
  <JBottomSheet v-model="dialog" hasPopstate>
    <template #header>
      <JContainer class="px-16 pt-24 text-pane-header font-bold text-dark">
        {{ $t('hotels') }} {{ $t('words.filters') }}
      </JContainer>
    </template>

    <div class="p-24">
      <HotelResultsFilter @submit="onSubmitForm" />
    </div>
  </JBottomSheet>

  <!-- DETAIL -->
  <JBottomSheet
    v-model="hotelDetailIsOpen"
    fullScreen
    hasPopstate
    @update:model-value="onHotelDetailUpdate"
  >
    <template #header>
      <JContainer class="flex justify-center px-16">
        <TheMobileHeader
          showBack
          :title="$t('book.hotelDetails')"
          titleClass="font-bold text-base flex-[10] px-16"
          @back="closeHotelDetail"
        />
      </JContainer>
    </template>

    <ServerError v-if="isError" @retry="refetch" />

    <HotelDetail
      v-else
      showAvailability
      :isLoading="hotelInfoIsFetching"
      @reserve="closeHotelDetail"
    />
  </JBottomSheet>
</template>

<script setup lang="ts">
import Swiper, { type Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { useToggle } from '@vueuse/core'
import { JBottomSheet, JButton, JContainer } from '@journey/ui-kit'
import useHotelSearchResultsStore from '@/modules/Search/store/hotelSearchResults'
import { useHotelInfoQuery } from '@/modules/Search/requests/queries'
import HotelCard from '@/modules/Core/components/shared/HotelCard/HotelCard.vue'
import { useSetItinerarySummary } from '@/modules/Search/composables'
import HotelCardSkeleton from '@/modules/Core/components/shared/HotelCard/HotelCardSkeleton/HotelCardSkeleton.vue'
import {
  DEFAULT_HOTEL_PAGE_SIZE,
  MINIMUM_RESULT_SIZE,
} from '@/modules/Search/constants'
import { useFilteredHotelListMutation } from '@/modules/Search/requests/mutations/useFilteredHotelListMutation'
import useHotelSearchFiltersStore from '@/modules/Search/store/hotelSearchFilter'
import type { FilteredHotelListPayload } from '@/modules/Search/types/api/payload/filteredHotelList'
import {
  SearchOrFilterMode,
  type SearchPanelHotelFilter,
} from '@/modules/Search/types'
import useSearchStore from '@/modules/Search/store'
import useSearchPanelHistoryStore from '@/modules/Search/store/searchPanelHistory'
import type { HotelInfo } from '@/modules/Search/types/model/hotelInfo'
import useAuthStore from '@/modules/Auth/store'
import featureFlag from '@/config/featureFlag'

defineOptions({
  inheritAttrs: false,
})

const MultiCitySearchResultSelector = defineAsyncComponent(
  () =>
    import(
      '@/modules/Search/components/MultiCitySearchResultSelector/MultiCitySearchResultSelector.vue'
    )
)
const HotelDetail = defineAsyncComponent(
  () => import('@/modules/Core/components/shared/HotelDetail/HotelDetail.vue')
)
const TheMobileHeader = defineAsyncComponent(
  () =>
    import('@/modules/Core/components/layout/MobileHeader/TheMobileHeader.vue')
)
const ServerError = defineAsyncComponent(
  () => import('@/modules/Core/components/shared/ServerError/ServerError.vue')
)
const HotelResultsFilter = defineAsyncComponent(
  () => import('../HotelResultsFilter/HotelResultsFilter.vue')
)
const NoSearchResult = defineAsyncComponent(
  () => import('@/modules/Search/components/NoSearchResult/NoSearchResult.vue')
)

const swiperRef = ref<SwiperType | null>(null)
const swiperElementRef = ref<HTMLElement | null>(null)
const swiperSlideIndex = ref(0)

onMounted(() => {
  swiperRef.value = new Swiper(swiperElementRef.value!, {
    slidesPerView: 1.3,
    spaceBetween: 10,
    on: {
      slideChange: onSlideChange,
    },
  })
})

const hotelSearchResultStore = useHotelSearchResultsStore()
const filteredHotelStore = useHotelSearchFiltersStore()
const searchPanelHistory = useSearchPanelHistoryStore()
const authStore = useAuthStore()

const searchOption = ref(0)
const customerCode = ref(featureFlag.customerCode)
const searchId = computed(() => searchPanelHistory.history.searchId ?? '')

const { handle } = useSetItinerarySummary()

// FILTERS
const apiPage = ref(1)
const size = ref(DEFAULT_HOTEL_PAGE_SIZE)
const query = ref('')
const city = ref()

const apiPayload = reactive<FilteredHotelListPayload>({
  searchId: '',
  sortBy: filteredHotelStore.filters.sortBy,
  bedType: filteredHotelStore.filters.bedType,
  roomType: filteredHotelStore.filters.roomType,
  viewType: filteredHotelStore.filters.viewType,
  starRating: filteredHotelStore.filters.starRating,
  meals: filteredHotelStore.filters.meals,
  hotels: filteredHotelStore.filters.hotels,
  query: query.value,
  page: apiPage.value,
  size: size.value,
  residency: authStore.userResidency,
})

const [dialog, setDialog] = useToggle(false)

const hotelFilterStore = useHotelSearchFiltersStore()

const { isLoading: hotelFiltersIsLoading, mutateAsync } =
  useFilteredHotelListMutation({
    meta: {
      noSuccessToast: true,
    },
  })

watch(hotelFiltersIsLoading, (value) => {
  hotelFilterStore.setLoading(value)
})

const searchStore = useSearchStore()
const [isLoadingFilter, toggleIsFilterLoading] = useToggle()

async function onSubmitFilters() {
  searchStore.setTriggeredBy(SearchOrFilterMode.Filter)

  apiPayload.searchId = searchId.value

  setDialog(false)

  const starRating =
    apiPayload.starRating === -1 ? undefined : apiPayload.starRating

  const { data } = await mutateAsync(
    {
      ...apiPayload,
      starRating,
    },
    {
      onError: () => {
        if (apiPayload.page === 1) {
          hotelSearchResultStore.setTotalHotelCount(0)
          hotelSearchResultStore.setTotalHotelPages(0)
        }
        toggleIsFilterLoading(false)
      },
    }
  )

  if (!data) {
    return
  }

  const { items, page } = data.data

  hotelSearchResultStore.appendHotels(items)

  if (page.items !== hotelSearchResultStore.totalHotelCount) {
    hotelSearchResultStore.setTotalHotelCount(page.items ?? 1)
  }
}

async function onSelectCity(value?: string) {
  toggleIsFilterLoading()
  city.value = value

  if (value) {
    apiPayload.destinations = [value.toLocaleLowerCase()]
  } else {
    delete apiPayload.destinations
  }

  apiPage.value = 1
  apiPayload.page = apiPage.value

  hotelSearchResultStore.resetHotels()
  swiperRef.value?.slideTo(0)

  await onSubmitFilters()

  toggleIsFilterLoading()
}

async function onSubmitForm(filters: SearchPanelHotelFilter) {
  toggleIsFilterLoading()

  apiPage.value = 1
  Object.assign(apiPayload, {
    sortBy: filters.sortBy,
    bedType: filters.bedType,
    roomType: filters.roomType,
    viewType: filters.viewType,
    starRating: filters.starRating,
    meals: filters.meals,
    hotels: filters.hotels,
    refundability: filters.refundability,
    query: query.value,
    size: null,
    page: 1,
  } as FilteredHotelListPayload)

  setDialog(false)

  hotelSearchResultStore.resetHotels()
  swiperRef.value?.slideTo(0)

  await onSubmitFilters()

  toggleIsFilterLoading()
}

const [hotelDetailIsOpen, setHotelDetailIsOpen] = useToggle(false)

function clearRooms() {
  hotelSearchResultStore.rooms = []
}

function onHotelDetailUpdate(value: boolean) {
  if (value) {
    return
  }

  clearRooms()
}

const {
  data: hotelDetail,
  isFetching: hotelInfoIsFetching,
  isError,
  refetch,
} = useHotelInfoQuery(searchId, searchOption, customerCode, {
  enabled: hotelDetailIsOpen,
})

async function openHotelDetail(option: number) {
  searchOption.value = option

  await nextTick()

  setHotelDetailIsOpen(true)

  if (hotelDetail.value?.data) {
    setHotelDetail(hotelDetail.value.data)
  }
}

function setHotelDetail(hotelInfo: HotelInfo) {
  hotelSearchResultStore.setSelectedHotel(hotelInfo)
}

function closeHotelDetail() {
  clearRooms()
  setHotelDetailIsOpen(false)
}

watch(hotelDetail, (value) => {
  if (!value) {
    return
  }

  if (!value.data) {
    return
  }

  hotelSearchResultStore.setSelectedHotel(value.data)
})

watch(isLoadingFilter, filteredHotelStore.setLoading)

const isLoadingSearchAndFilter = computed(
  () => hotelSearchResultStore.isLoading || isLoadingFilter.value
)

async function onSlideChange() {
  const activeIndex = swiperRef.value?.activeIndex

  if (activeIndex === undefined) {
    return
  }

  swiperSlideIndex.value = activeIndex

  if (
    hotelSearchResultStore.hotelsList.length ===
    hotelSearchResultStore.totalHotelCount
  ) {
    return
  }

  if ((activeIndex + 1) % (DEFAULT_HOTEL_PAGE_SIZE - 4) !== 0) {
    return
  }

  if (
    Math.ceil((activeIndex + 1) / DEFAULT_HOTEL_PAGE_SIZE) < apiPage.value &&
    apiPage.value !== 1
  ) {
    return
  }

  swiperRef.value?.update()

  await getFetchOrScrollHotelStatus()

  swiperRef.value?.update()
}

async function getFetchOrScrollHotelStatus() {
  if (isLoadingFilter.value) {
    return
  }

  apiPage.value++
  apiPayload.page = apiPage.value
  await onSubmitFilters()
}

watch(
  () => [
    hotelSearchResultStore.hotelsList.length,
    isLoadingSearchAndFilter.value,
  ],
  async () => {
    await nextTick()
    swiperRef.value?.update()
  }
)

watch(
  () => hotelSearchResultStore.isLoading,
  () => {
    apiPage.value = 1
  }
)

onBeforeUnmount(() => {
  swiperRef.value?.destroy()
})

defineExpose({
  onSelectCity,
})
</script>

<style lang="postcss" scoped>
.swiper {
  @apply z-0;
}
</style>
